<template>
  <div id="app">
    <main>
      <md-dialog :md-active.sync="OnBoarding">
        <md-steppers md-vertical :md-active-step.sync="active" md-linear>
          <md-step
            id="first"
            :md-label="$t('firstLabel')"
            :md-done.sync="first"
          >
            <p class="md-title">{{ $t("firstP1") }}</p>
            <p>{{ $t("firstP2") }}</p>
            <md-button
              class="md-raised md-primary"
              @click="setDone('first', 'second')"
              >{{ $t("next") }}</md-button
            >
          </md-step>

          <md-step
            id="second"
            :md-label="$t('secondLabel')"
            :md-done.sync="second"
          >
            <p v-html="$t('secondP1')"></p>
            <p v-html="$t('secondP2')"></p>
            <md-button
              v-if="installAppleHint"
              class="md-raised md-primary"
              @click="showOnBoarding = false"
              >{{ $t("letsgo") }}</md-button
            >
            <md-button
              v-else
              class="md-raised md-primary"
              @click="setDone('second', 'third')"
              >{{ $t("next") }}</md-button
            >
          </md-step>

          <md-step
            v-if="!installAppleHint"
            id="third"
            :md-label="$t('thirdLabel')"
          >
            <p>{{ $t("thirdP1") }}</p>
            <div v-if="installPrompt">
              <md-button @click="showOnBoarding = false">{{
                $t("noInstall")
              }}</md-button>
              <md-button class="md-raised md-primary" @click="install">{{
                $t("install")
              }}</md-button>
            </div>
            <div v-else>
              <md-button @click="showOnBoarding = false">{{
                $t("letsgo")
              }}</md-button>
            </div>
          </md-step>
        </md-steppers>
      </md-dialog>
      <md-dialog
        :md-active.sync="AppleHint"
        :md-fullscreen="false"
        class="apple-hint md-elevation-24"
      >
        <p v-html="$t('appleHint')"></p>
        <md-button @click="installAppleHint = false">{{
          $t("okay")
        }}</md-button>
      </md-dialog>

      <router-view :key="$route.fullPath"></router-view>
      <NotificationComponent />
    </main>
  </div>
</template>

<i18n>
  {
    "en": {
      "next": "Next",
      "letsgo": "Let's do this!",
      "okay": "Got it!",
      "noInstall": "Start without installing",
      "install": "Install and go!",
      "firstLabel": "Welcome",
      "firstP1": "Attention, dear course participants!",
      "firstP2": "With the DSLV ONLINE theory exam, you can ace your test from the comfort of your home. Once you've got the knowledge down, you'll get a certificate to prove it. To keep things easy and flexible, we've built this as an online platform instead of a traditional app—so you don't have to worry about compatibility issues.",
      "secondLabel": "How it works",
      "secondP1": "Your progress is saved directly on your device. So, before you dive in, make sure you stick to <strong>one</strong> device. Switching between devices won't work. Whether it's a tablet, phone, or PC—it's up to you, as long as you keep using the same one.",
      "secondP2": "Make sure your browser isn't set to delete your history when closing, especially over several days, or you might lose your progress!",
      "thirdLabel": "Install the Web App",
      "thirdP1": "Want a smoother experience? Install the web app on your device—it'll feel just like a real app. No distractions, and you can launch it straight from your home screen.",
      "appleHint": "On an Apple device? Just tap <img src='/img/iOS-action.svg' /> and select 'Add to Home Screen' <img src='/img/iOS-add.png' />."
    },
    "de": {
      "next": "Weiter",
      "letsgo": "Let's go!",
      "okay": "Okay",
      "noInstall": "Ohne Installation starten",
      "install": "Installieren und Starten",
      "firstLabel": "Intro",
      "firstP1": "Achtung werte Lehrgangsteilnehmer,",
      "firstP2": "die DSLV ONLINETheorieprüfung ermöglicht dir deine Theorieprüfung bequem und einfach von Zuhause aus zu bestehen. Nach erreichen des jeweiligen Wissensstandes stellt dir die Plattform ein Zertifikat über die bestandene Theorieprüfung aus. Wir möchten dir die Anwendung auf verschiedensten Geräten anbieten, daher bietet sich eine Online-Plattform an und keine „echte“ App, welche dann wieder mit einzelnen Betriebssystemen nicht kompatibel ist.",
      "secondLabel": "Anwendungshinweise",
      "secondP1": "Deine Zwischenstände werden in deinem lokalen Speicher abgelegt. Entscheide dich daher <strong>vor</strong> Beginn deiner Lerneinheiten für <strong>ein</strong> Gerät. Eine geräteübergreifende Nutzung ist nicht möglich. Welches Gerät du wählst (Tablett, Handy, PC) ist für die Nutzung irrelevant, solang du den Lernfortschritt mit diesem Gerät begleiten kannst.",
      "secondP2": "Versichere dich, dass dein Browser für die Zeit deines Lernens und die Zeiträume dazwischen (auch mehrerer Tage) keine Einstellung verwendet, welche z.B. selbstständig beim Beenden des Browsers deinen Verlauf löscht und lösche die Browserdaten auch nicht manuell.",
      "thirdLabel": "Webapp installieren",
      "thirdP1": "Du kannst dir die Webapp auf deinem Gerät installieren, dann verhält sie sich wie eine 'echte' App. Alle störenden Elemente sind ausgeblendet und sie ist durch ein Icon auf deinem Startscreen schneller erreichbar.",
      "appleHint": "Du kannst dir die Webapp auf deinem Apple Gerät installieren: tippe auf <img src='/img/iOS-action.svg' /> und wähle 'Zum Home-Bildschirm' <img src='/img/iOS-add.png' />"
    }
  }
</i18n>

<script>
import NotificationComponent, {
  Notification,
} from "./components/Notification.vue";

export default {
  name: "StepperLinear",
  components: {
    NotificationComponent,
  },
  methods: {
    install() {
      console.debug({ installPrompt: this.installPrompt });

      this.installPrompt.prompt();
      this.installPrompt.userChoice.then(
        (choiceResult) => console.debug({ choiceResult }),
        (error) => console.error({ error })
      );
      this.showOnBoarding = false;
    },
    setDone(id, index) {
      this[id] = true;

      this.secondStepError = null;

      if (index) {
        this.active = index;
      }
    },
  },
  async mounted() {
    window.addEventListener("beforeinstallprompt", (event) => {
      console.debug({ beforeinstallprompt: event });

      event.preventDefault();
      this.installPrompt = event;
    });

    window.addEventListener("appinstalled", (event) => {
      console.debug({ appinstalled: event });
      this.installPrompt = null;

      Notification.$emit("done", { text: "App erfolgreich installiert." });
    });

    this.installAppleHint =
      /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase()) &&
      !("standalone" in window.navigator && window.navigator.standalone);

    const detectDisplayMode = () => {
      let displayMode = "browser tab";
      if (navigator.standalone) {
        displayMode = "standalone-ios";
      }
      if (window.matchMedia("(display-mode: standalone)").matches) {
        displayMode = "standalone";
      }
      console.debug({ displayMode });
    };

    detectDisplayMode();
    window
      .matchMedia("(display-mode: standalone)")
      .addListener(detectDisplayMode);
  },
  computed: {
    OnBoarding() {
      return (
        this.showOnBoarding && (this.installPrompt || this.installAppleHint)
      );
    },
    AppleHint() {
      return this.installAppleHint && !this.OnBoarding;
    },
  },
  data: () => ({
    installPrompt: null,
    installAppleHint: false,
    showOnBoarding: true,
    active: "first",
    first: false,
    second: false,
    third: false,
  }),
};
</script>
<style>
body {
  overscroll-behavior-y: none;
}

.content {
  width: 100%;
  padding: 0 2%;
}

.apple-hint {
  min-height: 100%;
}

.apple-hint .md-dialog-container {
  border-radius: 0.5em 0.5em 0 0;
  padding: 20px;
  margin: 0;
  position: fixed;
  bottom: 0;
  max-width: 100%;
  z-index: 100;
}

.apple-hint p img {
  height: 1.5em;
}
</style>
